import { useState } from 'react';

import { useCreateStore, useDeleteStore, useStores, type StoreRow } from '../../api/queries.js';
import { useAuthStore } from '../../auth/auth-store.js';
import {
  Button,
  ConfirmModal,
  EmptyState,
  Field,
  IconButton,
  Input,
  PageHeader,
} from '../../components/ui/index.js';

/**
 * МАГАЗИНЫ
 *
 * Магазины связанной платформы появляются здесь сами — новый магазин там
 * сразу становится вкладкой во входящих со своими товарами и заказами;
 * управляет ими платформа. Вручную заводится только магазин без платформы
 * (свой сайт без модуля), чтобы развести по нему каналы и диалоги.
 */
export function SettingsStores(): JSX.Element {
  const slug = useAuthStore((s) => s.user?.workspaceSlug);
  const stores = useStores(slug).data ?? [];
  const create = useCreateStore(slug);
  const del = useDeleteStore(slug);
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [toDelete, setToDelete] = useState<StoreRow | null>(null);

  const linked = stores.filter((s) => s.partner_link_id);
  const manual = stores.filter((s) => !s.partner_link_id);

  return (
    <div className="inbx-page">
      <PageHeader title="Магазины" badge={{ count: stores.length }} />
      <div className="inbx-page__body" style={{ maxWidth: 760 }}>
        <p style={{ color: 'var(--inbx-text-secondary)', marginTop: 0 }}>
          У каждого магазина — своя вкладка во входящих, свои каналы и площадки, свои товары и
          заказы. Магазины платформы подключаются сами, в том числе созданные позже.
        </p>

        <h4 style={{ margin: '16px 0 8px' }}>С платформы</h4>
        {linked.length === 0 ? (
          <div style={{ color: 'var(--inbx-text-tertiary)' }}>
            Пока нет: свяжите платформу магазина в «Коннекторах данных».
          </div>
        ) : (
          linked.map((s) => <StoreLine key={s.id} store={s} />)
        )}

        <h4 style={{ margin: '24px 0 8px' }}>Свои</h4>
        {manual.length === 0 && (
          <EmptyState
            icon="ti-building-store"
            title="Своих магазинов нет"
            description="Заведите, если у вас есть сайт без платформы и его диалоги нужно держать отдельно."
          />
        )}
        {manual.map((s) => (
          <StoreLine key={s.id} store={s} onDelete={() => setToDelete(s)} />
        ))}
        <div className="inbx-card" style={{ marginTop: 12 }}>
          <Field label="Название">
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          <Field label="Адрес сайта" hint="Необязательно">
            <Input value={url} placeholder="https://" onChange={(e) => setUrl(e.target.value)} />
          </Field>
          <Button
            variant="primary"
            disabled={!name.trim() || create.isPending}
            onClick={() =>
              create.mutate(
                { name: name.trim(), ...(url.trim() ? { url: url.trim() } : {}) },
                {
                  onSuccess: () => {
                    setName('');
                    setUrl('');
                  },
                },
              )
            }
          >
            {create.isPending ? 'Добавляем…' : 'Добавить магазин'}
          </Button>
          {create.isError && (
            <div style={{ marginTop: 8, color: 'var(--inbx-danger, #dc2626)', fontSize: 13 }}>
              ✕ {(create.error as Error)?.message ?? 'Не удалось'}
            </div>
          )}
        </div>
      </div>
      <ConfirmModal
        open={toDelete !== null}
        title="Удалить магазин?"
        description={`Каналы и виджеты магазина «${toDelete?.name ?? ''}» станут общими, диалоги сохранятся.`}
        confirmLabel="Удалить"
        variant="danger"
        onCancel={() => setToDelete(null)}
        onConfirm={() => {
          if (toDelete) del.mutate(toDelete.id);
          setToDelete(null);
        }}
      />
    </div>
  );
}

function StoreLine({ store, onDelete }: { store: StoreRow; onDelete?: () => void }): JSX.Element {
  return (
    <div
      className="inbx-card"
      style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}
    >
      <i className="ti ti-building-store" style={{ fontSize: 22 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 500 }}>{store.name}</div>
        {store.url && (
          <div style={{ color: 'var(--inbx-text-tertiary)', fontSize: 'var(--inbx-fz-sm)' }}>
            {store.url}
          </div>
        )}
      </div>
      {!store.is_active && <span className="inbx-pill-badge">закрыт на платформе</span>}
      {onDelete && <IconButton icon="ti-trash" tooltip="Удалить" onClick={onDelete} />}
    </div>
  );
}
